import { useTranslations } from 'next-intl'
import { InputHTMLAttributes, Ref, useState } from 'react'
import { useController } from 'react-hook-form'

import { EyeClosedIcon, EyeOpenIcon } from '@radix-ui/react-icons'

import { TCommonFields } from '../../types'
import { FieldRow, getFieldRules } from '../field-row'
import { useFieldState } from '../hooks'
import { TControlledProps } from './input-field'

export const PasswordInput = (
    props: InputHTMLAttributes<HTMLInputElement> & {
        ref?: Ref<HTMLInputElement | null>
    }
) => {
    const t = useTranslations('form.password-field')
    const [visible, setVisible] = useState(false)

    return (
        <div className="form-password-field">
            <input {...props} ref={props.ref} type={visible ? 'text' : props.type} />
            <button
                type="button"
                aria-label={visible ? t('hide-password') : t('show-password')}
                aria-pressed={visible}
                disabled={props.disabled}
                onClick={() => setVisible(!visible)}
            >
                {visible ? <EyeOpenIcon /> : <EyeClosedIcon />}
            </button>
        </div>
    )
}

export type TPasswordField<T> = TCommonFields<T> & {
    type: 'password'
    labelTransition?: boolean
    props?: TControlledProps<InputHTMLAttributes<HTMLInputElement>>
}

type TProps<T> = {
    rowField: TPasswordField<T>
    fieldName: string
    labelWidth?: string
}

const PasswordField = <T,>({ rowField, fieldName, labelWidth }: TProps<T>) => {
    const { disabled, required } = useFieldState(rowField)

    const {
        field: { onChange, value, disabled: _disabled, ...fieldProps },
    } = useController({
        name: fieldName,
        rules: getFieldRules(rowField, required),
    })

    return (
        <FieldRow
            fieldName={fieldName}
            label={rowField.label}
            required={required}
            labelWidth={labelWidth}
            className="form-input"
            labelTransition={rowField.labelTransition}
            filled={rowField.labelTransition && !['', undefined, null].includes(value)}
        >
            <PasswordInput
                id={fieldName}
                {...fieldProps}
                {...rowField.props}
                value={value ?? ''}
                onChange={e => {
                    onChange(e.target.value)
                    rowField.onChange?.(e.target.value)
                }}
                type="password"
                required={required}
                disabled={disabled}
            />
        </FieldRow>
    )
}

export default PasswordField
